import { SizedBox } from '@/components/SizedBox'
import { TabPane, Tabs } from '@/components/Tabs'
import { useOrderListDividingStore } from '@/hooks/use-order-list-dividing'
import { useI18n } from 'i18n-utils'
import { ApiStatus, ContractServices } from 'network'
import {
  doubleMSocketService,
  useAssetsStore,
  useContractChangeSymbol,
  useDoubleContractAssetsAccountStore,
  useDoubleContractKlineStore,
  useDoubleContractOrderListStore,
  useQuoteStore,
  useResponsive,
  useUserInfoStore,
} from 'store'
import { StorageIndexDBEnum } from 'store/src/storage/StorageDB'
import { UiUtils, Visible } from 'ui-components'
import { OrderList } from './OrderList/OrderList'
import { TradeOrderer } from './Orderer'
import { ContractKline } from './components/ContractKline'
import { Handicap } from './components/Handicap'
import { HandicapRealTimeTransaction } from './components/HandicapRealTimeTransaction'
import { KlinePanel } from './components/KlinePanel'
import { RealTimeTransaction } from './components/RealTimeTransaction'
import './index.scss'

export enum TabType {
  /** 图表 */
  Chart = 'chart',
  /** 订单簿 */
  OrderBook = 'order-book',
  /** 最新成交 */
  RealTimeTransaction = 'real-time-transaction',
}

const TradeView = defineComponent({
  setup() {
    const klineStore = useDoubleContractKlineStore()
    const orderListDividingStore = useOrderListDividingStore()
    const quoteStore = useQuoteStore()
    const responsive = useResponsive()
    const { t } = useI18n()
    const activeTab = ref(TabType.Chart)
    const assetsStore = useAssetsStore()
    const userInfoStore = useUserInfoStore()
    const doubleContractAssetsAccountStore = useDoubleContractAssetsAccountStore()
    const route = useRoute()
    const isOnlyOpen = ref(false)
    const contractOrderListStore = useDoubleContractOrderListStore()

    watchEffect(() => {
      const symbolName = klineStore.getSymbolName()
      if (!symbolName) return
      const currentKline = klineStore.currentKline
      if (!currentKline) return
      document.title = `${symbolName} - ${currentKline?.close?.formatNumberWithCommas() || ''}`
    })

    onBeforeMount(async () => {
      quoteStore.listenContractM()
      assetsStore.fetchData()
      doubleContractAssetsAccountStore.fetchData()
      klineStore.currentSymbolId = route.query.symbol ? Number(route.query.symbol) : 1000001
    })

    onBeforeUnmount(() => {
      assetsStore.stopFetchData()
      doubleContractAssetsAccountStore.stopFetchData()
    })

    const height = computed(() => {
      if (typeof orderListDividingStore.mainElHeight === 'number') {
        return `${orderListDividingStore.mainElHeight}px`
      }
      return orderListDividingStore.mainElHeight
    })

    const subscribeAll = () => {
      klineStore.subscribeMarket()
      // 订阅盘口
      klineStore.subscribeHandicap()
      // 订阅实时成交
      klineStore.subscribeRealTimeTransaction()
    }

    useContractChangeSymbol(
      () => {
        subscribeAll()
      },
      { immediate: true },
    )

    onMounted(() => {
      doubleMSocketService.onReconnect(() => {
        subscribeAll()
      })
    })

    const openContract = async () => {
      if (doubleContractAssetsAccountStore.openContractFeature === '1') return

      UiUtils.showModal(t('合约交易属于高风险交易行为，在带来更多潜在收益的同时，也潜在巨大风险；请理性对待每一次交易行为，并且在市场出现剧烈波动时，您的余额有可能全部亏损'), {
        title: t('温馨提示'),
        confirmButtonText: t('我知道了'),
        onConfirm: async () => {
          const res = await ContractServices.openContract()
          if (res.status === ApiStatus.Success) {
            contractOrderListStore.getLimitOrders(true)
            contractOrderListStore.getIcebergStrategyOrders(true)
            doubleContractAssetsAccountStore.openContractFeature = '1'
            window.localStorage.setItem(StorageIndexDBEnum.ExchangeOpenContractFeature, '1')
          }
        },
      })
    }

    watch(
      () => [userInfoStore.isLogin, doubleContractAssetsAccountStore.doubleContractAssetsAccount?.openFutures],
      ([isLogin, openFutures]) => {
        if (isLogin && openFutures === 0 && !isOnlyOpen.value) {
          openContract()
          isOnlyOpen.value = true
        }
      },
      {
        immediate: true,
      },
    )

    return () => (
      <div class="trade-view-layout">
        <div class="trade-view-top" style={{ height: `${height}` }}>
          <div class="trading-view-container">
            <KlinePanel />
            {!responsive.isMobile && (
              <>
                <div class="kline-container">
                  <ContractKline />
                </div>
              </>
            )}
            {responsive.isMobile && (
              <Tabs tabHeight={42} v-model={activeTab.value}>
                <TabPane name={TabType.Chart} label={t('图表')}>
                  <ContractKline />
                </TabPane>
                <TabPane name={TabType.OrderBook} label={t('订单簿')}>
                  <Visible visible={activeTab.value === TabType.OrderBook} noVisibleIfRender={false}>
                    <Handicap />
                  </Visible>
                </TabPane>
                <TabPane name={TabType.RealTimeTransaction} label={t('最新成交')}>
                  <Visible visible={activeTab.value === TabType.RealTimeTransaction} noVisibleIfRender={false}>
                    <RealTimeTransaction />
                  </Visible>
                </TabPane>
              </Tabs>
            )}
          </div>
          {!responsive.isMobile && (
            <>
              <SizedBox width={4} />
              <HandicapRealTimeTransaction />
              <SizedBox width={4} />
              <TradeOrderer />
            </>
          )}
        </div>
        <OrderList />
        {responsive.isMobile && <TradeOrderer />}
      </div>
    )
  },
})

export default TradeView
